import { Alert, Button, Card, Divider, Input, message, Row, Select, Table, Tabs, Tag, Tooltip, Typography } from "antd";
import React, { useEffect } from "react";
import { FacturationGroup, Rg2sByOrga, UserPennylaneFacture } from "./facturation";
import { v4 } from "uuid";
import { InfoCircleOutlined } from "@ant-design/icons";
import {
    applyAgencySelectionChange,
    EMAIL_FACTURATION_REGEX,
    hasInvalidFacturationEmails,
} from "./facturationGroupsLogic";
import { facturationCgvService } from "./facturationCgvService";

export type FacturationOrganisationOption = {
    orga_id: string
    softaccess_id: string
    name: string
    defaultGroupId: string
}

interface FacturationGroupsProps {
    groups: FacturationGroup[],
    rg2s: Rg2sByOrga[],
    organisations?: FacturationOrganisationOption[],
    agencies?: { orga_id: string, agencies: { id: string, name: string }[] }[],
    saveFacturationGroupsCallback: (selectedOrga: string, groups: FacturationGroup[], rg2s: UserPennylaneFacture[]) => void,
    /** Modal Facturation : ferme le modal après save. Optionnel en mode Org. */
    setSettingsModalOpened?: React.Dispatch<React.SetStateAction<string | undefined>>,
    /** Mode page Org : orga déjà connue, pas de sélecteur. */
    lockedOrganisation?: FacturationOrganisationOption,
}

export function FacturationGroups(props: FacturationGroupsProps) {
    const lockedOrgaId = props.lockedOrganisation?.orga_id
    const [selectedOrga, setSelectedOrga] = React.useState<string | undefined>(lockedOrgaId);
    const [facturationGroups, setFacturationGroups] = React.useState<FacturationGroup[]>([]);
    const [agenciesFiltered, setAgenciesFiltered] = React.useState<{ id: string, name: string }[]>([]);
    const [rg2sFiltered, setRg2sFiltered] = React.useState<UserPennylaneFacture[]>([]);
    const [pleaseSave, setPleaseSave] = React.useState<boolean>(false);
    const [addGroupChooseBetween, setAddGroupChooseBetween] = React.useState<{ id: string, name: string }[] | undefined>(undefined)
    const [selectedAddGroupIds, setSelectedAddGroupIds] = React.useState<string[]>([]);
    const [saving, setSaving] = React.useState(false);

    useEffect(() => {
        if (lockedOrgaId) {
            setSelectedOrga(lockedOrgaId)
        }
    }, [lockedOrgaId])

    useEffect(() => {
        if (!selectedOrga) {
            setFacturationGroups([]);
            setRg2sFiltered([]);
            setAgenciesFiltered([]);
            return;
        }
        var groups = props.groups.filter(g => g.orgaId === selectedOrga);
        setFacturationGroups(groups);
        setRg2sFiltered(props.rg2s.find(el => el.orgaId === selectedOrga)?.rg2s || []);
        setAgenciesFiltered(props.agencies?.filter(value => value.orga_id === selectedOrga).flatMap(e => e.agencies).filter((value, index, self) =>
            index === self.findIndex((t) => (
                t.id === value.id
            ))) || []);
        setPleaseSave(false);
        setAddGroupChooseBetween(undefined);
        setSelectedAddGroupIds([]);
    }, [selectedOrga, props.groups, props.rg2s, props.agencies]);

    const softaccessId =
        props.lockedOrganisation?.softaccess_id ||
        props.organisations?.find(el => el.orga_id === selectedOrga)?.softaccess_id

    function addFacturationGroupClickedCallback() {
        if (!selectedOrga) return
        facturationCgvService.getAvailableNotUsedPennylaneFacturation(
            selectedOrga,
            facturationGroups.map(e => e.facturationGroupId)
        ).then((res) => {
            var data = res.data
            if (data.length == 0) {
            addGroupToExising(v4())
            } else {
            setAddGroupChooseBetween(data)
            }
        }, (_) => message.error("Une erreur est survenue lors de la récupération des groupes de facturations non utilisés dans pennylane")
        )
    }

    function addGroupToExising(id: string, otherId?: string, name1?: string, name2?: string) {
        const defaultGroupId =
            props.lockedOrganisation?.defaultGroupId ||
            props.organisations?.find(o => o.orga_id == selectedOrga && o.defaultGroupId != id)?.defaultGroupId

        let toAdd: FacturationGroup[] = [{ facturationGroupId: id, name: name1 || `group #${facturationGroups.length + 1}`, orgaId: selectedOrga!, agencies: [], default: false, emailFacturation: '' }];
        if (facturationGroups.length == 0) {
            toAdd = toAdd.concat([{ facturationGroupId: defaultGroupId || (otherId ? otherId : v4()), name: name2 || `group #${facturationGroups.length + 2}`, orgaId: selectedOrga!, agencies: agenciesFiltered, default: true, emailFacturation: '' }]);
        }
        setFacturationGroups([...facturationGroups, ...toAdd]);
        setPleaseSave(true);
    }

    const onAgencyChange = (groupId: string, val: string[]) => {
        const result = applyAgencySelectionChange(facturationGroups, agenciesFiltered, groupId, val)
        if (result.error) {
            message.error(result.error)
            return
        }
        setFacturationGroups(result.groups)
        setPleaseSave(true)
    }

    const saveChanges = () => {
        if (!selectedOrga || !softaccessId) return
        if (hasInvalidFacturationEmails(facturationGroups)) {
            message.error("Emails de facturation invalides")
            return
        }
        setSaving(true)
        facturationCgvService.saveFacturationGroups({
            orga_id: selectedOrga,
            softaccess_id: softaccessId,
            groups: facturationGroups,
            rg2s: rg2sFiltered,
        }).then(() => {
            message.success("Changements sauvegardés avec succès");
            props.saveFacturationGroupsCallback(selectedOrga, facturationGroups.filter(e => e.agencies.length > 0), rg2sFiltered);
            setPleaseSave(false);
            props.setSettingsModalOpened?.(undefined);
        }, () => message.error("Erreur lors de la sauvegarde des groupes"))
        .finally(() => setSaving(false))
    }

    const orgaName =
        props.lockedOrganisation?.name ||
        props.organisations?.find(o => o.orga_id == selectedOrga)?.name

    return (
        <>
            {!props.lockedOrganisation && (
                <>
                    <Typography.Text style={{ fontSize: '13px', marginBottom: '5px' }}>Choisis une organisation</Typography.Text>
                    <Select
                        showSearch
                        filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                        style={{ width: '100%' }}
                        placeholder="Sélectionner une organisation"
                        value={selectedOrga}
                        options={props.organisations?.filter(orga => orga.defaultGroupId.length > 0 ).map(orga => ({ label: orga.name, value: orga.orga_id }))}
                        onChange={setSelectedOrga}
                    />
                </>
            )}
            {selectedOrga && (
                <div>
                    <Tabs defaultActiveKey="groups" style={{ marginTop: props.lockedOrganisation ? 0 : '10px' }}>
                        <Tabs.TabPane key="groups" tab="Groupes" >
                            <Typography.Text style={{ fontSize: '13px' }}>Groupes de facturation pour l'organisation {orgaName}</Typography.Text>
                            <br />
                            <br />
                            <Button disabled={agenciesFiltered.length === 1 || addGroupChooseBetween != undefined} type="primary" style={{ marginBottom: '10px' }} onClick={addFacturationGroupClickedCallback}>Ajouter un groupe de facturation</Button>
                            {addGroupChooseBetween != undefined && (
                                <>
                                    <Alert
                                        message="Attention"
                                        description={`Afin d'éviter la création de groupes de facturation inutiles dans Pennylane, vous devez choisir ${addGroupChooseBetween.length == 2 && facturationGroups.length == 0 ? 2 : 1} id parmi les id d'organisations Pennylane non utilisés.`}
                                        type="warning"
                                        showIcon
                                        closable
                                    />
                                    <br />
                                    <Row style={{ marginTop: '10px', marginBottom: '10px' }}>
                                        <Select
                                            style={{ width: '300px', marginRight: '10px' }}
                                            placeholder="Sélectionner un/des groupes"
                                            mode="multiple"
                                            options={addGroupChooseBetween.map(e => ({ label: e.name, value: e.id }))}
                                            onChange={(e) => {
                                                var numberToChoose = addGroupChooseBetween.length == 2 && facturationGroups.length == 0 ? 2 : 1;
                                                if (e.length > numberToChoose) {
                                                    message.error(`Vous ne pouvez choisir que ${numberToChoose} id(s)`);
                                                    return;
                                                }
                                                setSelectedAddGroupIds(e as string[]);
                                            }}
                                            value={selectedAddGroupIds}
                                        />
                                        <Button type="primary" onClick={() => {
                                            setAddGroupChooseBetween(undefined)
                                            addGroupToExising(selectedAddGroupIds[0], selectedAddGroupIds.length == 2 ? selectedAddGroupIds[1] : undefined, addGroupChooseBetween.find(e => e.id === selectedAddGroupIds[0])?.name, selectedAddGroupIds.length == 2 ? addGroupChooseBetween.find(e => e.id === selectedAddGroupIds[1])?.name : undefined);
                                            setSelectedAddGroupIds([]);
                                        }} >Ok</Button>

                                    </Row>
                                    <Divider />
                                    </>

                                )}


                                    {facturationGroups.map((groupId) => (
                                        <Card key={groupId.facturationGroupId} style={{ marginBottom: '10px' }} title={
                                            <Row justify="space-between" align="middle" style={{ width: '100%' }}>
                                                <Typography.Text>{groupId.name}</Typography.Text>
                                                {groupId.default && (
                                                    <Row>
                                                        <Tag color="green">Par défaut</Tag>
                                                        <Tooltip title="Le groupe par défaut est celui qui sera utilisé pour les agences non assignées à un groupe de facturation. De même pour les utilisateurs rg2 non assignés à un groupe de facturation.">
                                                            <InfoCircleOutlined style={{ marginRight: '10px', cursor: 'pointer' }} />
                                                        </Tooltip>
                                                    </Row>
                                                )}
                                            </Row>
                                        }>
                                            <Typography.Text>Identifiant du groupe : {groupId.facturationGroupId}</Typography.Text>
                                            <Typography.Paragraph>Agence(s) : </Typography.Paragraph>
                                            <Select
                                                showSearch
                                                filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                                                style={{ width: '100%' }}
                                                placeholder="Sélectionner une/des agence(s)"
                                                options={agenciesFiltered.map(agency => ({ label: agency.name, value: agency.id }))}
                                                value={groupId.agencies.map(a => a.id)}
                                                mode="multiple"
                                                disabled={groupId.default}
                                                onChange={(val) => onAgencyChange(groupId.facturationGroupId, val)}
                                            />
                                            <Typography.Paragraph>Email de facturation : </Typography.Paragraph>
                                            <Input
                                                status={groupId.emailFacturation && !EMAIL_FACTURATION_REGEX.test(groupId.emailFacturation) ? "error" : ""}
                                                style={{ width: '300px' }}
                                                placeholder="Email de facturation"
                                                value={groupId.emailFacturation || ''}
                                                onChange={(e) => {
                                                    setFacturationGroups(facturationGroups.map(g => {
                                                        if (g.facturationGroupId === groupId.facturationGroupId) {
                                                            return {
                                                                ...g,
                                                                emailFacturation: e.target.value
                                                            };
                                                        } else {
                                                            return g;
                                                        }
                                                    }));
                                                    setPleaseSave(true);
                                                }}
                                            />
                                            {groupId.emailFacturation && !EMAIL_FACTURATION_REGEX.test(groupId.emailFacturation) && (
                                                <Typography.Text type="danger" style={{ display: 'block', marginTop: '5px' }}>
                                                    L'email de facturation doit être un email valide.
                                                </Typography.Text>
                                            )}
                                        </Card>
                                    ))}
                                </Tabs.TabPane>
                            <Tabs.TabPane key="rg2" tab="Gestion RG2" >
                                <Table
                                    dataSource={rg2sFiltered}
                                    rowKey={(el) => el.id || el.email}
                                    pagination={false}
                                    columns={[
                                        {
                                            title: 'Collaborateur',
                                            render: (_, el) => <Typography.Text>{el.name} {el.prename} - {el.email}</Typography.Text>
                                        },
                                        {
                                            title: 'Groupe de facturation',
                                            render: (_, el) => (
                                                <Select
                                                    style={{ width: '300px', marginLeft: '10px' }}
                                                    placeholder="Sélectionner un groupe de facturation"
                                                    options={facturationGroups.map((group) => ({ label: group.name, value: group.facturationGroupId }))}
                                                    value={facturationGroups.find(g => g.facturationGroupId === el.rg2GroupFacturationId)?.facturationGroupId}
                                                    onChange={(val) => {
                                                        setRg2sFiltered(rg2sFiltered.map(rg2 => {
                                                            if (rg2.email === el.email) {
                                                                return {
                                                                    ...rg2,
                                                                    rg2GroupFacturationId: val
                                                                };
                                                            } else {
                                                                return rg2;
                                                            }
                                                        }));
                                                        setPleaseSave(true);
                                                    }}
                                                />
                                            )
                                        }
                                    ]}
                                />
                            </Tabs.TabPane>
                    </Tabs>
                    <Button
                        type="primary"
                        loading={saving}
                        disabled={!pleaseSave || hasInvalidFacturationEmails(facturationGroups)}
                        onClick={saveChanges}
                    >
                        Sauvegarder les changements
                    </Button>
                </div>
            )}

        </>
    );
}
